import { Box, useTheme } from "@mui/material";
import useStyles from "./styles";
import React, { JSX, useEffect, useMemo, useRef, useState } from "react";
import Filters, { CustomFiltersRef } from "../../componentes/filters";
import api from "../../services/api";
import { LoadingPage } from "../loading";
import {
  useBlocker,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import ErroMessage from "../../componentes/errorMessage";
import CardsGrid from "./reactGridLayout";
import CardEditor from "./cardEditor";
import { v4 as uuidv4 } from "uuid";
import { useTemplate } from "../../context/templateContext";
import NewAlertModal from "../../componentes/newAlertModal";
import { useNotification } from "../../componentes/notificationProvider";
import { VerifyPermission } from "../../utils/verifyPermission";
import { groupCardsByParent } from "../../utils/groupCardsByParent";
import { FiltersProvider } from "../../context/filterContext";
import { usePages } from "../../context/pageContext";

type OutletContextType = {
  hideSideBar: boolean;
  setHideSideBar: React.Dispatch<React.SetStateAction<boolean>>;
  hideTopBar: boolean;
  setHideTopBar: React.Dispatch<React.SetStateAction<boolean>>;
  setPages: React.Dispatch<React.SetStateAction<Dash[]>>;
  setIsAutoRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  pages: Dash[];
};

const AUTO_REFRESH_TIME = 1 * 60 * 1000; // 1 min

function TemplatePage(): JSX.Element {
  const { pages } = usePages();
  const { page, card } = useParams();
  const theme = useTheme();
  const notify = useNotification();
  const colorMode = theme.palette.mode;
  const styles = useStyles(theme, colorMode, card);
  const navigate = useNavigate();
  const {
    editMode,
    setEditMode,
    setOnSave,
    setOnAddCard,
    setOnPreview,
    setOnExport,
    setOnDiscard,
    setOnBack,
    setShowBackButton,
    setIsTemplatePage,
    setPageTitle,
    setInitialPathName,
    initialPathName,
    hasChanges,
    setHasChanges,
  } = useTemplate();

  const childrenPages = useMemo(
    () => (pages ? getFilhoPagesMap(pages) : new Map<string, Dash>()),
    [pages],
  );

  const [pagina, setPagina] = useState<DashWithCardsGraphicsChildren>(
    childrenPages.values().next().value as DashWithCardsGraphicsChildren,
  );
  const [paginaState, setPaginaState] = useState({ ...pagina });
  const [cards, setCards] = useState<DashCardsGraphics[]>([]);
  const [loading, setLoading] = useState(false);
  const [firstLoading, setFirstLoading] = useState(true);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [boxWidth, setBoxWidth] = useState<number | null>(null);
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const [originalCards, setOriginalCards] = useState<DashCardsGraphics[]>([]);
  const [initialFilters, setInitialFilters] = useState<FiltersSend | null>(
    null,
  );
  const [filtersError, setFiltersError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const {
    hideSideBar,
    setHideSideBar,
    setPages,
    hideTopBar,
    setHideTopBar,
    setIsAutoRefresh,
  } = useOutletContext<OutletContextType>();
  const filtersRef = useRef<CustomFiltersRef>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const firstLoadDone = useRef(false);
  const isCardLoading = useRef(new Map<string, "loading" | "finished">());

  const paginaMemo = useMemo(() => paginaState, [paginaState]);
  const cardsMemo = useMemo(() => groupCardsByParent(cards), [cards]);

  useEffect(() => {
    if (page && childrenPages.has(page)) {
      setPagina(childrenPages.get(page) as DashWithCardsGraphicsChildren);
    } else {
      const firstFilhoPage = childrenPages.values().next().value;
      setPagina(firstFilhoPage as DashWithCardsGraphicsChildren);
    }
  }, [page, pages]);

  useEffect(() => {
    if (!pagina) return;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    // cardsAbortControllerRef.current?.abort();
    // cardsAbortControllerRef.current = null;

    setPaginaState({ ...pagina });
    setCards(pagina.cardsGraphics || []);
    setOriginalCards(pagina.cardsGraphics || []);
    setInitialFilters(null);
    setFiltersError(null);
    setFirstLoading(true);
    firstLoadDone.current = false;
    isCardLoading.current = new Map();

    if (pagina.cardsGraphics) {
      pagina.cardsGraphics.forEach((element) => {
        isCardLoading.current.set(element.id, "loading");
      });
    }
    setFiltersInitialized(false);
  }, [pagina?.id]);

  useEffect(() => {
    if (!paginaMemo || filtersInitialized) return;

    const initializePage = async () => {
      try {
        await fetchProcessedFilters();
      } catch (error) {
        setFiltersError("Erro ao carregar filtros");
      }
    };

    initializePage();

    handleResize();
    window.addEventListener("resize", handleResize);

    const interval = setInterval(() => {
      // handleDataRefresh();
    }, AUTO_REFRESH_TIME);

    return () => {
      window.removeEventListener("resize", handleResize);
      abortControllerRef.current?.abort();
      clearInterval(interval);
    };
  }, [filtersInitialized, paginaMemo?.id]);

  const handleDataRefresh = async () => {
    setIsAutoRefresh(true);

    setRefreshKey((prev) => prev + 1);
    setTimeout(() => setIsAutoRefresh(false), 2000);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      handleDataRefresh();
    }, AUTO_REFRESH_TIME);

    return () => clearInterval(interval);
  }, [paginaMemo?.id]);

  useEffect(() => {
    setIsTemplatePage(true);
    setPageTitle(paginaMemo.title);
    setInitialPathName(window.location.pathname);
    setShowBackButton(!!card);
    setOnSave(() => () => savePaginaInfo(cards));
    setOnAddCard(() => addNewCard);
    setOnPreview(() => () => {
      navigate(`${initialPathName}/${page}`);
      setEditMode(false);
    });

    setOnDiscard(() => descartarUpdate);
    setOnBack(() => () => navigate(initialPathName));

    return () => {
      setIsTemplatePage(false);
      setPageTitle("");
      setInitialPathName("");
      setShowBackButton(false);
      setOnSave(null);
      setOnAddCard(undefined);
      setOnPreview(null);
      setOnDiscard(null);
      setOnBack(null);
    };
  }, [
    cards,
    navigate,
    paginaMemo.title,
    card,
    initialPathName,
    setIsTemplatePage,
    setPageTitle,
    setShowBackButton,
    setOnSave,
    setOnAddCard,
    setOnPreview,
    setOnExport,
    setOnDiscard,
    setEditMode,
  ]);

  function getFilhoPagesMap(pages: Dash[]): Map<string, Dash> {
    const map = new Map<string, Dash>();

    const traverse = (pagesArr: Dash[]) => {
      pagesArr.forEach((page) => {
        if (page.type === "FILHO") {
          map.set(page.id, page);
        } else if (page.type === "PAI" && Array.isArray(page.subPages)) {
          traverse(page.subPages);
        }
      });
    };
    traverse(pages);
    return map;
  }

  const handleResize = () => {
    if (boxRef.current) {
      const { offsetWidth } = boxRef.current;
      setBoxWidth(offsetWidth);
    }
  };
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const shouldBlock =
      hasChanges &&
      !(
        currentLocation.pathname.includes(nextLocation.pathname) ||
        nextLocation.pathname.includes(currentLocation.pathname)
      );
    return shouldBlock;
  });

  function hasPositionLayoutDiff(
    cards: DashCardsGraphics[],
    paginaMemo: DashCardsGraphics[],
  ): boolean {
    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      const memoCard = paginaMemo.find((c) => c.id === card.id);
      const cardLayout = card.positionLayout
        ? Object.fromEntries(
            Object.entries(card.positionLayout).sort(([a], [b]) =>
              a.localeCompare(b),
            ),
          )
        : {};
      const memoCardLayout = memoCard?.positionLayout
        ? Object.fromEntries(
            Object.entries(memoCard.positionLayout).sort(([a], [b]) =>
              a.localeCompare(b),
            ),
          )
        : {};
      if (
        memoCard &&
        JSON.stringify(cardLayout) !== JSON.stringify(memoCardLayout)
      ) {
        return true;
      }
    }
    return false;
  }

  useEffect(() => {
    const isPositionDiff = hasPositionLayoutDiff(
      cards,
      paginaMemo.cardsGraphics,
    );
    if (isPositionDiff) {
      setHasChanges(true);
    }
  }, [cards]);

  useEffect(() => {
    if (blocker.state === "blocked") {
      setOpenAlertModal(true);
    }
  }, [blocker.state]);

  useEffect(() => {
    card ? setHideSideBar(true) : setHideSideBar(false);
  }, [card]);

  async function fetchProcessedFilters() {
    if (filtersInitialized) return;
    setFiltersLoading(true);
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await api.post(
        "/pages/findFilters",
        {
          filters: paginaState.filters,
        },
        { signal: controller.signal },
      );

      if (controller.signal.aborted) return;

      if (response.status === 200) {
        setPaginaState((prev) => ({
          ...prev,
          filters: response.data,
        }));

        const filterData = JSON.parse(localStorage.getItem("filters") || "{}");

        const transformedFilters = response.data.reduce(
          (acc: any, filter: any) => {
            if (filter.variable) {
              if (filter.type === "date") {
                let timeStart, timeEnd, option;
                if (filterData[pagina.id]?.date.timeStart) {
                  timeStart = new Date(filterData[pagina.id].date.timeStart);
                } else {
                  timeStart = new Date();
                  timeStart.setMonth(timeStart.getMonth() - 1);
                }
                timeStart.setSeconds(0, 0);

                if (filterData[pagina.id]?.date.timeEnd) {
                  timeEnd = new Date(filterData[pagina.id]?.date.timeEnd);
                } else {
                  timeEnd = new Date();
                }
                timeEnd.setSeconds(0, 0);
                option = filterData[pagina.id]?.date.option ?? null;
                acc[filter.variable] = {
                  timeStart,
                  timeEnd,
                  option,
                };
              } else if (filter.options) {
                if (
                  filterData[pagina.id] &&
                  filterData[pagina.id][filter.variable] !== undefined
                ) {
                  acc[filter.variable] = filterData[pagina.id][filter.variable];
                } else {
                  acc[filter.variable] = filter.options;
                }
              }
            }
            return acc;
          },
          {},
        );

        if (!controller.signal.aborted) {
          setInitialFilters(transformedFilters);
          setFiltersInitialized(true);
        }
      }
    } catch (error: any) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }
      setFiltersError("Erro ao carregar filtros");
      setFiltersInitialized(true);
    } finally {
      setFiltersLoading(false);
    }
  }

  const updateCard = (newCard: DashCardsGraphics) => {
    setCards((prevCards) => {
      const updatedCards = prevCards.map((card) =>
        card.id === newCard.id ? { ...card, ...newCard } : card,
      );
      if (JSON.stringify(prevCards) !== JSON.stringify(updatedCards)) {
        setHasChanges(true);
        return updatedCards;
      }
      return prevCards;
    });
  };

  function updateSubPageById<T extends Dash>(
    pages: T[],
    subPageId: string,
    newCard: DashCardsGraphics[],
  ): T[] {
    return pages.map((page) => {
      if (page.id === subPageId) {
        return { ...page, cardsGraphics: newCard };
      }
      if (Array.isArray(page.subPages) && page.subPages.length > 0) {
        return {
          ...page,
          subPages: updateSubPageById(page.subPages as T[], subPageId, newCard),
        };
      }
      return page;
    });
  }

  const savePaginaInfo = async (cardsData: any) => {
    try {
      if (!cardsData || !Array.isArray(cardsData)) {
        notify(400, "Informações inválidas para salvar informações.", "error");
      }

      const apiPayload = cardsData.map((card: any) => {
        const graphics = card?.graphics?.map((graphic: any) => {
          const { data, metrics, ...config } = graphic.config;
          return { ...graphic, config };
        });
        return { ...card, graphics };
      });

      const response = await api.put("/cards/updateMany", {
        graphics: apiPayload,
        pageId: paginaMemo.id,
      });

      if (response.status !== 200) {
        notify(
          response?.data?.code || 500,
          response?.data?.message ||
            "Erro ao salvar informações da página, codigo da resposta invalido.",
          "error",
        );
      } else {
        setPages((prevPages) =>
          updateSubPageById(prevPages, paginaState.id, cards),
        );
        setHasChanges(false);
        setOriginalCards(cards);
        notify(200, "Informações da página salvas com sucesso.", "success");
      }
    } catch (error: any) {
      notify(error?.status || 500, undefined, "error", error);
      console.error("Erro ao salvar informações da página:", error);
    }
  };

  const addNewCard = (fatherId?: string, maxYRepeat?: number) => {
    setCards((prev) => {
      const maxY = prev.reduce((max, card) => {
        const cardBottom =
          (card.positionLayout?.y || 0) + (card.positionLayout?.h || 0);
        return Math.max(max, cardBottom);
      }, 0);

      const newCard: DashCardsGraphics = {
        id: uuidv4(),
        size: { w: 8, h: 5, md: 5, sm: 12, xs: 24 },
        split: false,
        orientation: "horizontal" as any,
        background: true,
        height: 160,
        title: " ",
        description: " ",
        subtitle: " ",
        isChildren: !!fatherId,
        fatherId: fatherId,
        positionLayout: {
          h: 5,
          w: 8,
          x: 0,
          y: maxYRepeat ?? maxY,
        },
        graphics: [{ id: uuidv4(), config: {} }],
      };
      const updatedCards = [...prev, newCard];

      setPaginaState((prevState) => ({
        ...prevState,
        cardsGraphics: updatedCards,
      }));
      setHasChanges(true);
      return updatedCards;
    });
  };

  const duplicateCard = (card: DashCardsGraphics) => {
    setCards((prev) => {
      const maxY = prev.reduce((max, card) => {
        const cardBottom =
          (card.positionLayout?.y || 0) + (card.positionLayout?.h || 0);
        return Math.max(max, cardBottom);
      }, 0);

      const newCard: DashCardsGraphics = {
        ...card,
        id: uuidv4(),
        graphics: card.graphics.map((g) => ({ ...g, id: uuidv4() })),
        positionLayout: {
          w: card.positionLayout?.w ?? 8,
          h: card.positionLayout?.h ?? 5,
          x: card.positionLayout?.x ?? 0,
          y: maxY + (card.positionLayout?.h ?? 5),
        },
      };

      const updatedCards = [...prev, newCard];

      setPaginaState((prevState) => ({
        ...prevState,
        cardsGraphics: updatedCards,
      }));
      setHasChanges(true);
      return updatedCards;
    });
  };

  const deleteGraph = (id: string) => {
    setCards((prev) => {
      const updatedCards = prev.filter((card) => card.id !== id);
      setPaginaState((prevState) => ({
        ...prevState,
        cardsGraphics: updatedCards,
      }));
      setHasChanges(true);
      return updatedCards;
    });
  };

  const descartarUpdate = () => {
    setCards(originalCards);
    setHasChanges(false);
  };

  return (
    <FiltersProvider
      key={paginaMemo?.id}
      initialValues={initialFilters || {}}
      initialConfig={paginaMemo.filters}
    >
      <Box
        key={paginaMemo.title + "ContainerPage"}
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
        }}
        className="scrollable-container"
        ref={boxRef}
      >
        <Box sx={styles.container}>
          {paginaMemo.filters && (
            <Filters
              key={paginaMemo.title + "filters"}
              paginaId={paginaMemo?.id}
              ref={filtersRef}
              scrolled={false}
              hideTopBar={hideTopBar}
              filtersLoading={filtersLoading}
              boxWidth={boxWidth}
              // cardsLoading={cardsMemo.some(
              //   (element) =>
              //     isCardLoading.current.get(element.id) === "loading",
              // )}
              hideSideBar={hideSideBar}
              setHideSideBar={setHideSideBar}
              setHideTopBar={setHideTopBar}
            />
          )}

          {loading && <LoadingPage />}
          {!loading && (
            <ErrorBoundary FallbackComponent={ErroMessage as any}>
              {filtersError ? (
                <ErroMessage error={filtersError as any} />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    padding: paginaMemo.filters ? "0 1.5rem 1.5rem 1.5rem" : 3,
                  }}
                >
                  {!card ? (
                    <CardsGrid
                      cards={cardsMemo}
                      setCards={setCards}
                      editMode={editMode}
                      firstLoading={filtersLoading}
                      isCardLoading={isCardLoading}
                      handleDelete={deleteGraph}
                      duplicateCard={duplicateCard}
                      updateCard={updateCard}
                      paginaId={paginaMemo?.id}
                      filtersInitialized={filtersInitialized}
                      refreshKey={refreshKey}
                    />
                  ) : (
                    <VerifyPermission>
                      {(() => {
                        const filteredCard = cards.find((c) => c.id === card);
                        if (!filteredCard) {
                          navigate(`${initialPathName}`);
                          return <></>;
                        }
                        return (
                          <CardEditor
                            isCardLoading={isCardLoading}
                            firstLoading={filtersLoading}
                            fielteredCard={filteredCard}
                            updateCard={updateCard}
                            pageId={pagina.id}
                            handleDelete={deleteGraph}
                            filtersInitialized={filtersInitialized}
                          />
                        );
                      })()}
                    </VerifyPermission>
                  )}
                </Box>
              )}
            </ErrorBoundary>
          )}
          {/*         {administrador && (
          <PeekDrawer tabLabel="editar" width="40vw">
            <Typography variant="h6" gutterBottom>
              Painel de Edição
            </Typography>
            <Divider sx={{ marginBottom: 2 }} />
            <Stack spacing={2} height={"100%"}>
              <JsonEditor
                json={paginaMemo.cardsGraphics.map((card) => {
                  const graphics = card?.graphics?.map((graphic) => {
                    const { data, metrics, ...config } = graphic.config;
                    return { ...graphic, config };
                  });
                  return { ...card, graphics };
                })}
                onSave={savePaginaInfo}
                onApply={applyPaginaInfo}
              />
            </Stack>
          </PeekDrawer>
        )} */}
        </Box>
        {/* {!id && (
        <Fab
          aria-label="toggle"
          sx={styles.flotaingButton}
          onClick={() => {
            setHideSideBar(!hideSideBar);
            setHideTopBar(!hideTopBar);
          }}
          variant="circular"
        >
          {!hideSideBar && !hideTopBar ? <OpenInFull /> : <CloseFullscreen />}
        </Fab>
      )} */}
        <NewAlertModal
          openModal={openAlertModal}
          setOpenModal={setOpenAlertModal}
          buttonText="Salvar"
          buttonCancelText="Sair sem salvar"
          confirmar={false}
          handleButton={() => {
            savePaginaInfo(cards);
            setOpenAlertModal(false);
            blocker?.proceed?.();
          }}
          handleCancel={() => {
            setHasChanges(false); // Mark as saved to allow navigation
            setOpenAlertModal(false);
            blocker?.proceed?.();
          }}
          severity="warning"
          singleButton={false}
          textContent="Você tem alterações não salvas, deseja salvar?"
        />
      </Box>
    </FiltersProvider>
  );
}
export default TemplatePage;
